import { IntegrationType, integrationPublicType } from '@/features/integrations';
import type { Integration } from '@/services/db/integration/entity';

const PIPELINES_PREFIX = 'pipelines_';

/** Colour tokens the type label uses, per public type. */
type TypeLabel = { text: string; color: 'purple' | 'green' | 'clementine' };

export function hasPipelinesPrefix(id: string): boolean {
  return id.startsWith(PIPELINES_PREFIX);
}

/**
 * A catalog id read back as a name: the backend serializes spaces to
 * underscores, and prefixes pipelines entries with their direction.
 */
export function normalizeName(id: string | undefined): string {
  if (id == null) {
    return '';
  }

  const withoutPrefix = hasPipelinesPrefix(id)
    ? id.replace('pipelines_Source_', '').replace('pipelines_Destination_', '')
    : id;

  return withoutPrefix.replace(/_/g, ' ');
}

/**
 * The breadcrumb's leaf.
 *
 * A pipelines entry's id is a slug rather than a name (`pipelines_Source_bigquery`),
 * so those use the catalog's own name; every other id normalizes cleanly.
 */
export function crumbName(integration: Pick<Integration, 'id' | 'name'>): string {
  return hasPipelinesPrefix(integration.id) ? integration.name : normalizeName(integration.id);
}

/** How a data-flow type is described to the reader. */
export function typeName(type: string): string {
  if (type === IntegrationType.pipelinesSource) {
    return 'Data in';
  }
  if (type === IntegrationType.pipelinesDestination) {
    return 'Data out';
  }
  return type;
}

/**
 * The TYPE chip, or `null` for a public type with no chip of its own.
 *
 * Every type the workspace catalog serves today folds into `Existing`, so the
 * other two branches are for catalog entries an account cannot connect yet.
 */
export function typeLabel(integration: Pick<Integration, 'type'>): TypeLabel | null {
  const publicType = integrationPublicType(integration);

  if (publicType === IntegrationType.connectPartner) {
    return { text: 'Connect Partners', color: 'purple' };
  }
  if (publicType === IntegrationType.existing) {
    return { text: `Existing Integration (${typeName(integration.type)})`, color: 'green' };
  }
  if (publicType === IntegrationType.nonExistent) {
    return { text: 'Potential Integration', color: 'clementine' };
  }
  return null;
}

/** Whether the Connect Partner banner belongs above the detail grid. */
export function isConnectPartner(integration: Pick<Integration, 'type'>): boolean {
  return integrationPublicType(integration) === IntegrationType.connectPartner;
}
